import { useMutation, useQuery } from '@apollo/client';
import { gql } from '__generated__';
import { PrintSessionStatusEnumType } from '__generated__/graphql';
import { FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Button from 'components/Button';
import ConfirmButton from 'components/ConfirmButton';
import DHLIdentityCard from 'components/DHLIdentityCard';
import ErrorMessage from 'components/ErrorMessage';
import FAIdentityCard from 'components/FAIdentityCard';
import LoadingIndicator from 'components/LoadingIndicator';
import MOEIdentityCard from 'components/MOEIdentityCard';
import NHCIdentityCard from 'components/NHCIdentityCard';

import theme from './theme.module.scss';

const PRINT_SESSION_QUERY = gql(`
  query PrintSession($id: ID!) {
    printSession(id: $id) {
      id
      name
      status
      createdAt
      department {
        id
        name
      }
      identityCards {
        id
        cardNumber
        user {
          id
          firstName
          lastName
          gender
          department {
            id
            name
          }
          nationality
        }
        employee {
          id
          organisationCode
          fileNumber
          division {
            id
            name
          }
          designation {
            id
            name
          }
          signature
          photo
        }
        codeLink
        status
        issueDate
        expiryDate
        updatedAt
        createdAt
      }
    }
  }
`);

const MARK_PRINT_SESSION_AS_PRINTED = gql(`
  mutation MarkPrintSessionAsPrinted($id: ID!) {
    markPrintSessionAsPrinted(id: $id) {
      id
      status
    }
  }
`);

const MARK_PRINT_SESSION_AS_CANCELLED = gql(`
  mutation MarkPrintSessionAsCancelled($id: ID!) {
    markPrintSessionAsCancelled(id: $id) {
      id
      status
    }
  }
`);

const PrintSession: FC = () => {
  const navigate = useNavigate();
  const { printSessionId } = useParams<{ printSessionId: string }>() as { printSessionId: string };

  const { loading, error, data, refetch } = useQuery(PRINT_SESSION_QUERY, {
    variables: {
      id: printSessionId,
    },
  });

  const [markPrintSessionAsPrinted, { loading: markPrintLoading, error: markPrintError }] =
    useMutation(MARK_PRINT_SESSION_AS_PRINTED, {
      refetchQueries: ['DepartmentIdentityCards'],
    });

  const [markPrintSessionAsCancelled, { loading: markCancelLoading, error: markCancelError }] =
    useMutation(MARK_PRINT_SESSION_AS_CANCELLED);

  if (loading) return <LoadingIndicator />;

  if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

  const printSession = data.printSession;

  function getIdentityCardByDepartment(departmentId, identityCardData) {
    if (departmentId === '20009c7b-5c40-4d60-b3cc-9ea4c801112e') {
      return <MOEIdentityCard identityCard={identityCardData} />;
    }
    if (departmentId === 'c95fa4c3-34ab-4b6f-82ad-c36abdefe198') {
      return <FAIdentityCard identityCard={identityCardData} />;
    }
    if (departmentId === 'feac5faf-8cfd-4a06-969c-2a87e1f257b6') {
      return <DHLIdentityCard identityCard={identityCardData} />;
    }
    if (departmentId === 'a2a13993-2b58-4833-a702-5b582d3e5f72') {
      return <NHCIdentityCard identityCard={identityCardData} />;
    }
  }

  return (
    <div className={theme.container}>
      {printSession.identityCards.map(identityCard => {
        return getIdentityCardByDepartment(printSession.department.id, identityCard);
      })}
      <div className={theme.printSessionCard}>
        <p className={theme.title}>Print Session</p>
        <p className={theme.name}>
          Name: <strong>{printSession.name}</strong>
        </p>
        <p className={theme.department}>Department: {printSession.department.name}</p>
        <p className={theme.status}>Status: {printSession.status}</p>
        <p className={theme.date}>Created At: {printSession.createdAt}</p>
        {printSession.status === PrintSessionStatusEnumType.Pending ? (
          <div className={theme.actionsContainer}>
            <Button onClick={() => window.print()}>Print</Button>
            <ConfirmButton
              onConfirm={() =>
                markPrintSessionAsPrinted({ variables: { id: printSessionId } }).then(() => {
                  navigate(`/departments/${printSession.department.id}/identity-cards`);
                })
              }
              loading={markPrintLoading}
              error={markPrintError}
            >
              Mark Cards as Active
            </ConfirmButton>
            <ConfirmButton
              onConfirm={() =>
                markPrintSessionAsCancelled({ variables: { id: printSessionId } }).then(() => {
                  navigate(`/departments/${printSession.department.id}/identity-cards`);
                })
              }
              loading={markCancelLoading}
              error={markCancelError}
            >
              Cancel Printing
            </ConfirmButton>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default PrintSession;
